import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { PendingDose } from '@rondela/shared'
import type { TimelineItem } from '../lib/timeline'
import { formatLaterItemsTimes } from '../lib/timeline'
import { TimelineItemRow } from './TimelineItemRow'

interface LaterItemsChipProps {
  items: TimelineItem[]
  onDoseTaken: (dose: PendingDose) => void
  onDoseMissed: (dose: PendingDose) => void
  isLoading?: boolean
}

/**
 * Collapsed chip showing count of later items with expandable list
 * Shows: "+N dosis posteriores (14:00, 20:00)"
 */
export function LaterItemsChip({
  items,
  onDoseTaken,
  onDoseMissed,
  isLoading = false,
}: LaterItemsChipProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (items.length === 0) return null

  const times = formatLaterItemsTimes(items)
  const label = items.length === 1 ? 'dosis posterior' : 'dosis posteriores'

  const handleDoseAction = (item: TimelineItem, action: 'taken' | 'missed') => {
    if (item.type === 'dose') {
      if (action === 'taken') {
        onDoseTaken(item.data)
      } else {
        onDoseMissed(item.data)
      }
    }
  }

  return (
    <div className="later-items-chip-container">
      <button
        type="button"
        className="later-items-chip"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="later-items-chip__text">
          +{items.length} {label} ({times})
        </span>
        <span className="later-items-chip__chevron">
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {isExpanded && (
        <div className="later-items-chip__expanded">
          {items.map((item, index) => (
            <TimelineItemRow
              key={item.type === 'dose' ? item.data.id : `apt-${item.data.id}-${index}`}
              item={item}
              onDoseTaken={() => handleDoseAction(item, 'taken')}
              onDoseMissed={() => handleDoseAction(item, 'missed')}
              isLoading={isLoading}
              showActions={item.type === 'dose'}
            />
          ))}
        </div>
      )}
    </div>
  )
}
